import React, { useEffect, useState, useCallback } from "react";
import { Star, CheckCircle, XCircle, Trash2, Clock, RefreshCw } from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";

type AdminYorum = {
  yorumId: number; urunId: number; urunAd: string;
  musteriId: number; musteriAdSoyad: string;
  puan: number; yorumMetni?: string;
  onayliMi: boolean; olusturmaTarihi: string;
};

const Stars: React.FC<{ value: number }> = ({ value }) => (
  <div className="inline-flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <Star key={s} size={13}
        className={s <= value ? "fill-amber-400 text-amber-400" : "text-slate-200 fill-slate-200"} />
    ))}
  </div>
);

const AdminYorumlarPage: React.FC = () => {
  const [yorumlar, setYorumlar] = useState<AdminYorum[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"hepsi" | "bekleyen" | "onaylı">("bekleyen");
  const [page, setPage] = useState(1);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [toplamAdet, setToplamAdet] = useState(0);
  const [busy, setBusy] = useState<Record<number, boolean>>({});

  const load = useCallback(async (p = 1, f = filter) => {
    try {
      setLoading(true);
      const res = await axiosInstance.post("/UrunYorumlari/admin/listele", {
        page: p, pageSize: 20,
        sadeceBekleyenler: f === "bekleyen",
      });
      if (res.data?.success) {
        const d = res.data.data;
        // filtre "onaylı" ise client-side filtrele
        const list: AdminYorum[] = d.yorumlar ?? [];
        setYorumlar(f === "onaylı" ? list.filter(x => x.onayliMi) : list);
        setToplamSayfa(d.toplamSayfa ?? 1);
        setToplamAdet(d.toplamAdet ?? 0);
        setPage(p);
      }
    } catch { /* yoksay */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(1, filter); }, [filter]);

  const setOnay = async (yorumId: number, onay: boolean) => {
    setBusy(b => ({ ...b, [yorumId]: true }));
    try {
      await axiosInstance.post("/UrunYorumlari/admin/onayla", { yorumId, onayliMi: onay });
      setYorumlar(prev => prev.map(y =>
        y.yorumId === yorumId ? { ...y, onayliMi: onay } : y
      ));
      if (filter === "bekleyen" && onay) {
        setYorumlar(prev => prev.filter(y => y.yorumId !== yorumId));
      }
    } finally { setBusy(b => ({ ...b, [yorumId]: false })); }
  };

  const sil = async (yorumId: number) => {
    if (!window.confirm("Bu yorumu silmek istediğinize emin misiniz?")) return;
    setBusy(b => ({ ...b, [yorumId]: true }));
    try {
      await axiosInstance.post("/UrunYorumlari/admin/sil", { yorumId });
      setYorumlar(prev => prev.filter(y => y.yorumId !== yorumId));
      setToplamAdet(t => t - 1);
    } finally { setBusy(b => ({ ...b, [yorumId]: false })); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Yorum Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-0.5">Toplam {toplamAdet} yorum</p>
        </div>
        <button
          onClick={() => load(page, filter)}
          className="h-9 px-4 rounded-xl border bg-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-slate-50"
        >
          <RefreshCw size={14} />
          Yenile
        </button>
      </div>

      {/* Filtre */}
      <div className="flex gap-2 mb-5">
        {(["bekleyen", "onaylı", "hepsi"] as const).map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={[
              "h-9 px-4 rounded-xl text-sm font-semibold border transition",
              filter === f
                ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white border-transparent"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
            ].join(" ")}
          >
            {f === "bekleyen" ? "⏳ Bekleyenler" : f === "onaylı" ? "✅ Onaylılar" : "📋 Hepsi"}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : yorumlar.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Star size={32} className="mx-auto mb-2 text-slate-200" />
          {filter === "bekleyen" ? "Bekleyen yorum yok 🎉" : "Henüz yorum yok"}
        </div>
      ) : (
        <div className="space-y-3">
          {yorumlar.map(y => (
            <div
              key={y.yorumId}
              className={[
                "rounded-2xl border bg-white p-4 transition",
                !y.onayliMi ? "border-l-4 border-l-amber-400" : "border-l-4 border-l-green-400",
              ].join(" ")}
            >
              <div className="flex flex-wrap items-start gap-3">
                {/* Sol: bilgi */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-800">{y.musteriAdSoyad}</span>
                    <Stars value={y.puan} />
                    {y.onayliMi
                      ? <span className="text-[11px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Onaylı</span>
                      : <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock size={10}/>Bekliyor</span>
                    }
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    <span className="font-medium text-slate-600">{y.urunAd}</span>
                    {" · "}
                    {new Date(y.olusturmaTarihi).toLocaleDateString("tr-TR")}
                  </div>
                  {y.yorumMetni && (
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{y.yorumMetni}</p>
                  )}
                </div>

                {/* Sağ: aksiyonlar */}
                <div className="flex items-center gap-2 shrink-0">
                  {!y.onayliMi ? (
                    <button
                      onClick={() => setOnay(y.yorumId, true)}
                      disabled={busy[y.yorumId]}
                      className="h-9 px-3 rounded-xl text-xs font-bold text-white bg-green-500 hover:bg-green-600 transition inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <CheckCircle size={13} />
                      Onayla
                    </button>
                  ) : (
                    <button
                      onClick={() => setOnay(y.yorumId, false)}
                      disabled={busy[y.yorumId]}
                      className="h-9 px-3 rounded-xl text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <XCircle size={13} />
                      Geri Al
                    </button>
                  )}
                  <button
                    onClick={() => sil(y.yorumId)}
                    disabled={busy[y.yorumId]}
                    className="h-9 w-9 rounded-xl text-red-500 border border-red-100 hover:bg-red-50 transition inline-flex items-center justify-center disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Sayfalama */}
          {toplamSayfa > 1 && (
            <div className="flex justify-center gap-2 pt-3">
              <button onClick={() => load(page-1)} disabled={page<=1}
                className="h-9 px-4 rounded-xl border bg-white text-sm disabled:opacity-30 hover:bg-slate-50">← Önceki</button>
              <span className="h-9 px-4 rounded-xl border bg-white text-sm inline-flex items-center">
                {page} / {toplamSayfa}
              </span>
              <button onClick={() => load(page+1)} disabled={page>=toplamSayfa}
                className="h-9 px-4 rounded-xl border bg-white text-sm disabled:opacity-30 hover:bg-slate-50">Sonraki →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminYorumlarPage;
